import React, { useState, useEffect } from 'react';
import { BarChart, CheckCircle, Clock, FileText, ShieldCheck, TrendingUp, AlertCircle, Search, Award, Save, Edit, X, Upload, Plus, Trash2 } from 'lucide-react';
import Button from '../components/UI/Button';
import Input from '../components/UI/Input';
import { api } from '../services/api';
import { SiteContent, ValuationContent } from '../types';
import { useAuth } from '../context/AuthContext';
import EditableContent from '../components/Admin/EditableContent';

// Helper for Icons
const IconMap: any = {
  FileText: FileText,
  Search: Search,
  BarChart: BarChart,
  Award: Award,
  TrendingUp: TrendingUp,
  ShieldCheck: ShieldCheck
};

const ValuationPage: React.FC = () => {
  const { user } = useAuth();
  const [content, setContent] = useState<SiteContent | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    assetType: 'brand',
    description: ''
  });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const pageContent = await api.getPageContent();
      setContent(pageContent);
    };
    fetchData();
  }, []);

  const handleSaveContent = async () => {
    if (!content) return;
    setSaving(true);
    await api.updatePageContent(content);
    setSaving(false);
    setIsEditing(false);
  };

  const updateValuationContent = (section: keyof ValuationContent, key: string, value: any) => {
    if (!content) return;
    setContent({
      ...content,
      valuation: {
        ...content.valuation,
        [section]: typeof content.valuation[section] === 'object' && !Array.isArray(content.valuation[section])
          ? { ...(content.valuation[section] as any), [key]: value }
          : value
      }
    });
  };

  const updateWhyItem = (index: number, field: string, value: string) => {
    if (!content) return;
    const newItems = [...content.valuation.why.items];
    newItems[index] = { ...newItems[index], [field]: value };
    updateValuationContent('why', 'items', newItems);
  };

  const updateStepItem = (index: number, field: string, value: any) => {
    if (!content) return;
    const newSteps = [...content.valuation.steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    updateValuationContent('steps', 'newSteps', newSteps); // The helper above handles the actual update
    
    // Correcting the helper usage for arrays
    setContent({
        ...content,
        valuation: {
            ...content.valuation,
            steps: newSteps
        }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'https://tbiz.ir'}/backend/api.php?path=valuation-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSubmitted(true);
        setFormData({ name: '', phone: '', assetType: 'brand', description: '' });
        setTimeout(() => setSubmitted(false), 5000);
      } else {
        alert('خطا در ثبت درخواست. لطفاً دوباره تلاش کنید.');
      }
    } catch (error) {
      console.error('Error submitting valuation request:', error);
      alert('خطا در ارتباط با سرور. لطفاً دوباره تلاش کنید.');
    }
  };

  if (!content || !content.valuation) return <div className="min-h-screen flex items-center justify-center">در حال بارگذاری...</div>;

  const isAdmin = user?.role === 'admin';
  const { valuation } = content;

  return (
    <div className="bg-gray-50 min-h-screen pb-20 relative">
      {/* Admin Toolbar */}
      {isAdmin && (
        <div className="fixed bottom-6 left-6 z-50 flex gap-2">
            {isEditing ? (
                <>
                    <Button onClick={handleSaveContent} disabled={saving} className="shadow-xl bg-green-600 hover:bg-green-700 flex items-center gap-2">
                        <Save size={18}/> {saving ? 'ذخیره...' : 'ذخیره تغییرات'}
                    </Button>
                    <Button onClick={() => { setIsEditing(false); window.location.reload(); }} variant="ghost" className="bg-white shadow-xl text-red-500 hover:bg-red-50">
                        <X size={18}/>
                    </Button>
                </>
            ) : (
                <Button onClick={() => setIsEditing(true)} className="shadow-xl bg-gray-900 hover:bg-gray-800 flex items-center gap-2">
                    <Edit size={18}/> ویرایش صفحه
                </Button>
            )}
        </div>
      )}

      {/* Hero Section */}
      <section className="bg-[#1e3a5f] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-black mb-6">
            <EditableContent 
                isEditing={isEditing}
                value={valuation.hero.title}
                onChange={(val) => updateValuationContent('hero', 'title', val)}
                className="text-center bg-transparent border-white/20"
            />
          </h1>
          <div className="text-lg text-blue-100 max-w-3xl mx-auto leading-relaxed">
            <EditableContent 
                isEditing={isEditing}
                type="textarea"
                value={valuation.hero.subtitle}
                onChange={(val) => updateValuationContent('hero', 'subtitle', val)}
                className="text-center bg-transparent border-white/20"
            />
          </div>
        </div>
      </section>

      {/* Why Valuation? */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
                <EditableContent 
                    isEditing={isEditing}
                    value={valuation.why.title}
                    onChange={(val) => updateValuationContent('why', 'title', val)}
                />
            </h2>
            <div className="space-y-6">
              {valuation.why.items.map((item, idx) => {
                const Icon = IconMap[item.icon] || TrendingUp;
                return (
                  <div key={idx} className="flex gap-4">
                    <div className="shrink-0 w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center">
                      {isEditing ? (
                        <select 
                            value={item.icon}
                            onChange={(e) => updateWhyItem(idx, 'icon', e.target.value)}
                            className="bg-transparent text-[10px] w-full outline-none"
                        >
                            {Object.keys(IconMap).map(k => <option key={k} value={k}>{k}</option>)}
                        </select>
                      ) : <Icon size={24} />}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-1">
                        <EditableContent 
                            isEditing={isEditing}
                            value={item.title}
                            onChange={(val) => updateWhyItem(idx, 'title', val)}
                        />
                      </h3>
                      <div className="text-gray-600">
                        <EditableContent 
                            isEditing={isEditing}
                            type="textarea"
                            value={item.desc}
                            onChange={(val) => updateWhyItem(idx, 'desc', val)}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="bg-white p-10 rounded-3xl shadow-lg border border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">درخواست ارزش‌گذاری</h3>
            
            {submitted ? (
              <div className="bg-green-50 text-green-700 p-6 rounded-2xl flex flex-col items-center text-center animate-[fadeIn_0.5s_ease-out]">
                <CheckCircle size={48} className="mb-4" />
                <h4 className="font-bold text-lg mb-2">درخواست شما با موفقیت ثبت شد</h4>
                <p className="text-sm">کارشناسان ما به زودی با شما تماس خواهند گرفت.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input 
                  label="نام و نام خانوادگی"
                  placeholder="مثال: علی محمدی"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
                <Input 
                  label="شماره تماس"
                  placeholder="مثال: 09123456789"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  required
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">نوع دارایی</label>
                  <select 
                    className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                    value={formData.assetType}
                    onChange={(e) => setFormData({...formData, assetType: e.target.value})}
                  >
                    <option value="brand">برند و علامت تجاری</option>
                    <option value="patent">اختراع و دانش فنی</option>
                    <option value="business">کسب‌وکارهای آماده</option>
                    <option value="software">نرم‌افزار و اپلیکیشن</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">توضیحات کوتاه</label>
                  <textarea 
                    className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all h-24"
                    placeholder="توضیح مختصری در مورد دارایی خود بنویسید..."
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                  ></textarea>
                </div>
                <Button type="submit" className="w-full bg-[#1e3a5f] hover:bg-[#152a45] text-white rounded-xl py-4 font-bold">
                  ثبت درخواست مشاوره رایگان
                </Button>
                <p className="text-xs text-gray-400 text-center flex items-center justify-center gap-1">
                  <AlertCircle size={12} /> اطلاعات شما نزد ما محفوظ می‌ماند.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">فرآیند ارزش‌گذاری در TBIZ</h2>
            <p className="text-gray-600">ما از ابتدا تا انتهای مسیر در کنار شما هستیم.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {valuation.steps.map((step, idx) => {
              const Icon = IconMap[step.icon] || FileText;
              return (
                <div key={step.id} className="relative p-8 rounded-3xl bg-gray-50 border border-gray-100 text-center group hover:bg-white hover:shadow-xl transition-all">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm font-bold text-[#1e3a5f] border border-gray-100">
                    {step.id}
                  </div>
                  <div className="mb-4 flex justify-center">
                     {isEditing ? (
                        <select 
                            value={step.icon}
                            onChange={(e) => updateStepItem(idx, 'icon', e.target.value)}
                            className="bg-transparent text-xs outline-none"
                        >
                            {Object.keys(IconMap).map(k => <option key={k} value={k}>{k}</option>)}
                        </select>
                     ) : <Icon size={24} className="text-blue-600" />}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    <EditableContent 
                        isEditing={isEditing}
                        value={step.title}
                        onChange={(val) => updateStepItem(idx, 'title', val)}
                    />
                  </h3>
                  <div className="text-sm text-gray-500 leading-relaxed">
                    <EditableContent 
                        isEditing={isEditing}
                        type="textarea"
                        value={step.description}
                        onChange={(val) => updateStepItem(idx, 'description', val)}
                    />
                  </div>
                  {idx < 3 && (
                    <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-px bg-gray-200"></div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ValuationPage;
